/**
 * Three-layer device detection per Combined Addendum 2026-04-30 §7.
 *
 * Layer 1 — Form factor (size/pointer): drives layout decisions
 * Layer 2 — Platform (OS/skin/browser): drives native conventions, fonts, safe areas
 * Layer 3 — Capabilities (features): drives feature gating
 *
 * Form factor alone is not enough. A 6.1" Galaxy S22 and a 6.1" iPhone share
 * viewport dimensions but render very differently — we need OS skin to pick
 * the right font stack, touch-target floor, gesture-reservation padding.
 */

export type FormFactor =
  | 'phone_portrait'
  | 'phone_landscape'
  | 'tablet_portrait'
  | 'tablet_landscape'
  | 'desktop'
  | 'desktop_wide';

export type Platform = 'ios' | 'ipados' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown';
export type OSSkin = 'ios' | 'one_ui' | 'pixel' | 'stock_android' | 'desktop' | 'unknown';
export type Browser = 'safari' | 'chrome' | 'firefox' | 'edge' | 'samsung_internet' | 'unknown';
export type BrowserEngine = 'webkit' | 'blink' | 'gecko' | 'unknown';

export interface DeviceProfile {
  // Layer 1 — Form factor
  formFactor: FormFactor;
  viewportWidth: number;
  viewportHeight: number;
  orientation: 'portrait' | 'landscape';
  hasFinePointer: boolean;
  hasCoarsePointer: boolean;
  hasHover: boolean;

  // Layer 2 — Platform
  platform: Platform;
  platformVersion: string | null;
  osSkin: OSSkin;
  browser: Browser;
  browserEngine: BrowserEngine;

  // Layer 3 — Capabilities
  hasCamera: boolean;
  hasBluetooth: boolean;
  hasGeolocation: boolean;
  hasVibration: boolean;
  hasSpeechRecognition: boolean;
  hasNotchOrCutout: boolean;
  prefersDarkMode: boolean;
  prefersReducedMotion: boolean;
  isPWA: boolean;
  isStandalone: boolean;
}

/* ------------------------------------------------------------------ */
/* Layer 2 — Platform / browser detection                             */
/* ------------------------------------------------------------------ */

interface UANavigator extends Navigator {
  userAgentData?: {
    platform: string;
    brands: { brand: string; version: string }[];
    mobile: boolean;
    getHighEntropyValues?: (hints: string[]) => Promise<Record<string, string>>;
  };
}

function detectBrowser(ua: string): { browser: Browser; engine: BrowserEngine } {
  // Order matters — Edge contains "Chrome", Samsung Internet contains "Chrome",
  // Chrome contains "Safari". Test the most specific signature first.
  if (/SamsungBrowser/.test(ua)) return { browser: 'samsung_internet', engine: 'blink' };
  if (/Edg\//.test(ua)) return { browser: 'edge', engine: 'blink' };
  if (/Firefox|FxiOS/.test(ua)) return { browser: 'firefox', engine: 'gecko' };
  if (/Chrome|CriOS/.test(ua)) {
    // CriOS = Chrome on iOS, which uses WebKit under the hood (App Store rule).
    return { browser: 'chrome', engine: /CriOS/.test(ua) ? 'webkit' : 'blink' };
  }
  if (/Safari/.test(ua)) return { browser: 'safari', engine: 'webkit' };
  return { browser: 'unknown', engine: 'unknown' };
}

function detectPlatform(): Pick<DeviceProfile, 'platform' | 'platformVersion' | 'osSkin' | 'browser' | 'browserEngine'> {
  if (typeof navigator === 'undefined') {
    return { platform: 'unknown', platformVersion: null, osSkin: 'unknown', browser: 'unknown', browserEngine: 'unknown' };
  }
  const ua = navigator.userAgent || '';
  const uad = (navigator as UANavigator).userAgentData;

  // Modern: User-Agent Client Hints (Chromium-based browsers). Synchronous low-entropy
  // values are enough for skin/platform; full version requires async getHighEntropyValues.
  let platformHint: string | null = uad?.platform ?? null;
  let mobileHint: boolean | null = uad?.mobile ?? null;

  // iOS / iPadOS — Safari is not a Client Hints participant, so UA-string parsing
  // is still the primary signal. iPadOS 13+ Safari reports as Mac (desktop-class
  // browsing); detect the iPad via maxTouchPoints.
  const isAppleTouch = /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
  if (isAppleTouch) {
    const isIPad = /iPad/.test(ua) ||
      (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints ?? 0) > 1);
    const versionMatch = ua.match(/OS (\d+)[_.](\d+)/);
    const version = versionMatch ? `${versionMatch[1]}.${versionMatch[2]}` : null;
    const { browser, engine } = detectBrowser(ua);
    return {
      platform: isIPad ? 'ipados' : 'ios',
      platformVersion: version,
      osSkin: 'ios',
      browser,
      browserEngine: engine,
    };
  }

  // Android — distinguish Samsung One UI from Pixel from stock.
  if (/Android/.test(ua) || platformHint === 'Android') {
    const isSamsung = /SamsungBrowser/.test(ua) || /SM-|GT-|SAMSUNG/i.test(ua);
    const isPixel = /Pixel/.test(ua);
    const versionMatch = ua.match(/Android (\d+(?:\.\d+)?)/);
    const { browser, engine } = detectBrowser(ua);
    return {
      platform: 'android',
      platformVersion: versionMatch ? versionMatch[1] : null,
      osSkin: isSamsung ? 'one_ui' : isPixel ? 'pixel' : 'stock_android',
      browser,
      browserEngine: engine,
    };
  }

  // Desktop — fall through with platform hints.
  if (/Macintosh|Mac OS X/.test(ua) || platformHint === 'macOS') {
    const { browser, engine } = detectBrowser(ua);
    return { platform: 'macos', platformVersion: null, osSkin: 'desktop', browser, browserEngine: engine };
  }
  if (/Windows/.test(ua) || platformHint === 'Windows') {
    const { browser, engine } = detectBrowser(ua);
    return { platform: 'windows', platformVersion: null, osSkin: 'desktop', browser, browserEngine: engine };
  }
  if (/Linux/.test(ua) || platformHint === 'Linux') {
    const { browser, engine } = detectBrowser(ua);
    return { platform: 'linux', platformVersion: null, osSkin: 'desktop', browser, browserEngine: engine };
  }
  // Silence "unused" warning for mobileHint — kept for future tuning.
  void mobileHint;
  return { platform: 'unknown', platformVersion: null, osSkin: 'unknown', browser: 'unknown', browserEngine: 'unknown' };
}

/* ------------------------------------------------------------------ */
/* Layer 1 — Form factor                                              */
/* ------------------------------------------------------------------ */

function detectFormFactor(width: number, height: number): FormFactor {
  // Width breakpoints align with Tailwind defaults / Foundation §7.2.
  // We treat the longer dimension as the canonical width-equivalent so a
  // landscape iPhone is still classified as a phone.
  const longer = Math.max(width, height);
  const isPortrait = height >= width;

  if (longer < 768) return isPortrait ? 'phone_portrait' : 'phone_landscape';
  if (longer < 1280) return isPortrait ? 'tablet_portrait' : 'tablet_landscape';
  if (longer < 1920) return 'desktop';
  return 'desktop_wide';
}

/* ------------------------------------------------------------------ */
/* Layer 3 — Capabilities                                             */
/* ------------------------------------------------------------------ */

interface SpeechWindow extends Window {
  SpeechRecognition?: unknown;
  webkitSpeechRecognition?: unknown;
}

function detectCapabilities(): Pick<
  DeviceProfile,
  'hasCamera' | 'hasBluetooth' | 'hasGeolocation' | 'hasVibration' |
  'hasSpeechRecognition' | 'hasNotchOrCutout' | 'prefersDarkMode' |
  'prefersReducedMotion' | 'isPWA' | 'isStandalone'
> {
  if (typeof window === 'undefined') {
    return {
      hasCamera: false, hasBluetooth: false, hasGeolocation: false,
      hasVibration: false, hasSpeechRecognition: false, hasNotchOrCutout: false,
      prefersDarkMode: false, prefersReducedMotion: false,
      isPWA: false, isStandalone: false,
    };
  }
  const w = window as SpeechWindow;
  const nav = navigator as Navigator & { bluetooth?: unknown; standalone?: boolean };

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    Boolean(nav.standalone) ||
    document.referrer.startsWith('android-app://');

  // env(safe-area-inset-top) > 0 implies a cutout, notch, or rounded corner.
  // Read by injecting a probe element since CSS env() can't be accessed directly.
  let safeTop = 0;
  try {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;top:env(safe-area-inset-top);visibility:hidden;pointer-events:none';
    document.documentElement.appendChild(probe);
    safeTop = parseFloat(getComputedStyle(probe).top || '0') || 0;
    probe.remove();
  } catch { /* SSR or pre-DOM */ }

  return {
    hasCamera: 'mediaDevices' in navigator && 'getUserMedia' in (navigator.mediaDevices ?? {}),
    hasBluetooth: 'bluetooth' in nav,
    hasGeolocation: 'geolocation' in navigator,
    hasVibration: 'vibrate' in navigator,
    hasSpeechRecognition: 'SpeechRecognition' in w || 'webkitSpeechRecognition' in w,
    hasNotchOrCutout: safeTop > 0,
    prefersDarkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
    prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    isPWA: isStandalone,
    isStandalone,
  };
}

/* ------------------------------------------------------------------ */
/* Public — combine all three layers                                  */
/* ------------------------------------------------------------------ */

export function detectDevice(): DeviceProfile {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  const orientation: 'portrait' | 'landscape' = h >= w ? 'portrait' : 'landscape';

  const platform = detectPlatform();
  const caps = detectCapabilities();

  // Pointer media queries — fine = mouse/trackpad; coarse = touchscreen.
  const hasFinePointer =
    typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches;
  const hasCoarsePointer =
    typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;
  const hasHover =
    typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches;

  return {
    formFactor: detectFormFactor(w, h),
    viewportWidth: w,
    viewportHeight: h,
    orientation,
    hasFinePointer,
    hasCoarsePointer,
    hasHover,
    ...platform,
    ...caps,
  };
}

/**
 * Apply the detected profile to the document root as data-* attributes.
 * Stylesheets target `[data-platform="android"][data-os-skin="one_ui"]`
 * etc. for platform-specific rules per Addendum §7.9.
 */
export function applyDeviceAttributes(profile: DeviceProfile) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.dataset.platform = profile.platform;
  html.dataset.osSkin = profile.osSkin;
  html.dataset.browser = profile.browser;
  html.dataset.formFactor = profile.formFactor;
  html.dataset.orientation = profile.orientation;
  html.dataset.standalone = profile.isStandalone ? 'true' : 'false';
  html.dataset.pointer = profile.hasFinePointer ? 'fine' : profile.hasCoarsePointer ? 'coarse' : 'none';
}
